import { api } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'
import { Field, Select } from '@/components/ui/form'
import type { LeadAssignee, LeadAssignmentMode, Paged, SalesTeamListItem } from '@/types/api'

export interface VipRule {
  mode: LeadAssignmentMode | null
  userId: string | null
  teamId: string | null
}

/**
 * Who a Very Important lead goes to, when that is not whoever gets every other lead.
 *
 * <p>
 * A contact form and an ad mapping ask the same question, so they share this. "Same as other
 * leads" sends nothing, and the source's ordinary rule applies - which is what every source
 * did before company research existed. The person and team lists are the same ones the
 * ordinary rule picks from: somebody who could not open a lead in this country is never
 * offered.
 * </p>
 */
export function VipAssignmentFields({
  countryId,
  rule,
  onChange,
  enabled = true,
}: {
  countryId: string
  rule: VipRule
  onChange: (rule: VipRule) => void
  /** False while the surrounding dialog is closed, so nothing is fetched for it. */
  enabled?: boolean
}) {
  const people = useAsync<LeadAssignee[]>(
    (signal) =>
      enabled && rule.mode === 'FixedUser' && countryId
        ? api.get<LeadAssignee[]>('/leads/assignees', { countryId }, signal)
        : Promise.resolve([]),
    [enabled, rule.mode, countryId],
  )

  const teams = useAsync<SalesTeamListItem[]>(
    (signal) =>
      enabled && rule.mode === 'RoundRobin'
        ? api
            .get<Paged<SalesTeamListItem>>('/sales-teams', { pageSize: 200, countryId }, signal)
            .then((paged) => paged.items)
        : Promise.resolve([]),
    [enabled, rule.mode, countryId],
  )

  return (
    <>
      {/* Its own row: a second rule, read after the first rather than tangled into it. */}
      <div aria-hidden className="border-t border-line-soft sm:col-span-2" />
      <Field
        label="Very Important leads go to"
        hint="Decided after the company has been researched. Junk goes to nobody."
      >
        {(id) => (
          <Select
            id={id}
            value={rule.mode ?? ''}
            onChange={(e) => {
              const mode = (e.target.value || null) as LeadAssignmentMode | null
              onChange({ mode, userId: null, teamId: null })
            }}
          >
            <option value="">Same as other leads</option>
            <option value="Unassigned">Nobody - the country's unassigned queue</option>
            <option value="FixedUser">One person</option>
            <option value="RoundRobin">A sales team, in turn</option>
          </Select>
        )}
      </Field>

      {rule.mode === 'FixedUser' && (
        <Field label="Person for Very Important leads" required hint="Only people who can open leads in this country.">
          {(id) => (
            <Select
              id={id}
              disabled={people.loading}
              value={rule.userId ?? ''}
              onChange={(e) => onChange({ ...rule, userId: e.target.value || null })}
            >
              <option value="">Choose somebody</option>
              {(people.data ?? []).map((person) => (
                <option key={person.userId} value={person.userId}>
                  {person.displayName}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}

      {rule.mode === 'RoundRobin' && (
        <Field label="Team for Very Important leads" required hint="Each one goes to the next member, in turn.">
          {(id) => (
            <Select
              id={id}
              disabled={teams.loading}
              value={rule.teamId ?? ''}
              onChange={(e) => onChange({ ...rule, teamId: e.target.value || null })}
            >
              <option value="">Choose a team</option>
              {(teams.data ?? [])
                .filter((team) => team.isActive || team.id === rule.teamId)
                .map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
            </Select>
          )}
        </Field>
      )}
    </>
  )
}
